import React, { type Dispatch, type SetStateAction } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { Button, Modal, setSnack, useManualModalState } from "@synthesis/shared-ui";
import { findByProjectIdQueryKey, useDelete } from "@synthesis/shared-services/models/modelsDrawingsController/hooks";

import { useStores } from "../../../store";

interface ModalDeleteDrawingProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  openModal: () => void;
  closeModal: () => void;
}

const ModalDeleteDrawing = React.memo((props: ModalDeleteDrawingProps) => {
  return (
    <Modal.Root manual {...props}>
      <ModalDeleteDrawingContent />
    </Modal.Root>
  );
});

const ModalDeleteDrawingContent = React.memo(() => {
  const { generalStore } = useStores();
  const queryClient = useQueryClient();
  const deleteDrawing = useDelete();
  const { closeModal } = useManualModalState();

  const checkedCount = generalStore.checkedFiles.size;
  const projectId = generalStore.projectId ?? undefined;

  const handleConfirm = async () => {
    if (checkedCount === 0 || deleteDrawing.isPending) return;

    const ids = Array.from(generalStore.checkedFiles.keys());

    try {
      for (const drawingId of ids) {
        await deleteDrawing.mutateAsync({ path: { drawingId } });
      }

      if (projectId) {
        await queryClient.invalidateQueries({ queryKey: findByProjectIdQueryKey({ path: { projectId } }) });
      }

      generalStore.resetCheckedAll();
      closeModal();

      setSnack({
        title: checkedCount > 1 ? "Чертежи удалены" : "Чертёж удалён",
        messageType: "info",
      });
    } catch {
      setSnack({
        title: checkedCount > 1 ? "Не удалось удалить чертежи" : "Не удалось удалить чертёж",
        messageType: "error",
      });
    }
  };

  const title = `Удаление ${checkedCount > 1 ? "нескольких файлов" : "файла"}`;

  return (
    <Modal.Content className="w-[35vw] min-w-[350px]">
      <Modal.Title>{title}</Modal.Title>
      <div className="px-5 pb-2">
        <h2 className="mt-2">Вы уверены, что хотите безвозвратно удалить {checkedCount > 1 ? "выбранные файлы?" : "этот файл?"}</h2>
        <div className="my-2 flex content-center justify-between">
          <Button type="button" theme="primary" disabled={deleteDrawing.isPending} onClick={handleConfirm}>
            Удалить {checkedCount > 1 ? "файлы" : "файл"}
          </Button>
          <Button type="button" onClick={() => closeModal()}>
            Отменить
          </Button>
        </div>
      </div>
    </Modal.Content>
  );
});

export default ModalDeleteDrawing;
